import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import { formatPercentage, formatNumber } from '@/lib/utils/format';
import type { DiscountDepthData } from '../types/analytics.types';

const tooltipStyle = {
  backgroundColor: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '8px',
  color: 'hsl(var(--foreground))',
};

const BAR_COLORS = ['#93c5fd', '#60a5fa', '#3b82f6', '#2563eb', '#1d4ed8', '#1e40af'];

interface DiscountTabProps {
  data: DiscountDepthData | null;
  isLoading: boolean;
}

export function DiscountTab({ data, isLoading }: DiscountTabProps) {
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <Card className="p-4">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-8 w-20" />
          </Card>
          <Card className="p-4">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-8 w-20" />
          </Card>
        </div>
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-[350px] w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Descuento Promedio</p>
          <p className="text-2xl font-bold mt-1 text-blue-600">
            {formatPercentage(data.avgDiscount)}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Total Cotizaciones</p>
          <p className="text-2xl font-bold mt-1">{formatNumber(data.totalQuotes, 0)}</p>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Distribución de Descuentos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[350px]">
            {data.buckets.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.buckets}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12 }}
                    className="fill-muted-foreground"
                  />
                  <YAxis tick={{ fontSize: 12 }} className="fill-muted-foreground" />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={
                      ((value: any, name: string) => {
                        if (name === 'count')
                          return [formatNumber(Number(value ?? 0), 0), 'Cotizaciones'];
                        return [value ?? 0, name];
                      }) as any
                    }
                    labelFormatter={(label) => `Rango: ${label}`}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {data.buckets.map((_, index) => (
                      <Cell key={index} fill={BAR_COLORS[index % BAR_COLORS.length]} />
                    ))}
                    <LabelList
                      dataKey="percentage"
                      position="top"
                      formatter={((v: any) => `${Number(v).toFixed(0)}%`) as any}
                      className="fill-muted-foreground"
                      style={{ fontSize: 11 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                Sin datos de descuentos para este periodo
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
